import classNames from 'classnames';
import React from 'react';

import './InputContainer.scss';

/** @typedef {HTMLInputElement & HTMLSelectElement} InputContainerEle */
/** @typedef {import('react').Ref<InputContainerEle>} InputContainerRef */

/**
 * @typedef InputContainerProps
 * @property {boolean} [labelAlwaysHover=false]
 * @property {string} [icon]
 */
/** @type {import('~/types/React').ReactFC<InputContainerProps>} */
const InputContainer = ({ icon, labelAlwaysHover = false, ...rest }) => {
	const /** @type {InputContainerRef} */ fieldRef = React.useRef(null);
	const [fieldIsFocused, setFieldIsFocused] = React.useState(false);

	const curValue = fieldRef.current?.value;
	const hasValue = !!curValue;

	const onBlur = () => setFieldIsFocused(false);
	const onFocus = () => setFieldIsFocused(true);

	// When our fieldRef is ready, attach our blur and focus listeners to the elements.
	React.useEffect(() => {
		if (!fieldRef.current) {
			return;
		}

		// Attach our listeners.
		fieldRef.current.addEventListener('blur', onBlur);
		fieldRef.current.addEventListener('focus', onFocus);
	}, [fieldRef]);

	/**
	 * @param {import('~/types/React').DefaultProps['children']} children
	 * @param {string | number} innerKey
	 */
	function getProcessedChildren(children, innerKey = 'unknown-key') {
		if (!children && typeof children !== 'object') {
			return children;
		} else if (Array.isArray(children)) {
			return children.map(getProcessedChildren);
		} else if (['input', 'select'].includes(children.type ?? '')) {
			// Add the ref to the desired element.
			return React.cloneElement(children, {
				key: innerKey ?? 'unknownId',
				ref: fieldRef,
			});
		}

		return children;
	}

	return (
		<div
			className={classNames(
				'input-container-div',
				fieldIsFocused ? 'focused' : 'not-focused',
				labelAlwaysHover || hasValue || fieldIsFocused ? 'opaque' : 'not-opaque',
				icon ? 'label-icon' : 'no-label-icon',
				rest.className
			)}
			id={rest.id}
		>
			{!!icon && (
				<div aria-hidden="true" className="icon">
					{icon}
				</div>
			)}

			{getProcessedChildren(rest.children, 'main-children')}
		</div>
	);
};

export default InputContainer;
